/* Street Lines — one orchestrated motion system.
 *
 * Contract: markup is visible by default. This script adds `sl-motion` to
 * <html>, which is the only thing that arms the hidden-then-reveal CSS. If the
 * script never runs, is blocked, or throws, the page still reads correctly —
 * nothing can be left stranded invisible.
 *
 * Two behaviours, one timing curve:
 *   [data-sl-reveal]  — fade/slide up when scrolled into view
 *   .sl-linemask      — masked line reveal, used for display type
 * Children of [data-sl-stagger] inherit an incremental --sl-delay.
 */
(() => {
  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Honour the OS setting: leave everything in its resting state.
  if (reduced.matches) return;
  if (!('IntersectionObserver' in window)) return;

  root.classList.add('sl-motion');

  const STAGGER_MS = 90;

  const prepare = (scope) => {
    scope.querySelectorAll('[data-sl-stagger]').forEach((group) => {
      const step = Number(group.dataset.slStagger) || STAGGER_MS;
      const targets = group.querySelectorAll(':scope > [data-sl-reveal], :scope > * > .sl-linemask');
      targets.forEach((el, i) => {
        el.style.setProperty('--sl-delay', `${i * step}ms`);
      });
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        // One-shot: nothing re-hides on scroll back up.
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
  );

  const observe = (scope) => {
    prepare(scope);
    scope.querySelectorAll('[data-sl-reveal], .sl-linemask').forEach((el) => {
      if (el.classList.contains('is-in')) return;
      observer.observe(el);
    });
  };

  const init = () => observe(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Anything above the fold should play immediately rather than waiting for a
  // scroll event that may never come.
  window.addEventListener('load', () => {
    document.querySelectorAll('[data-sl-reveal], .sl-linemask').forEach((el) => {
      const box = el.getBoundingClientRect();
      if (box.top < window.innerHeight) el.classList.add('is-in');
    });
  });

  // Theme editor: newly injected sections need arming too.
  document.addEventListener('shopify:section:load', (event) => {
    observe(event.target);
    event.target.querySelectorAll('[data-sl-reveal], .sl-linemask').forEach((el) => {
      el.classList.add('is-in');
    });
  });

  // If the visitor turns reduced-motion on mid-session, stop hiding things.
  const onPrefChange = (event) => {
    if (!event.matches) return;
    root.classList.remove('sl-motion');
    observer.disconnect();
  };
  if (reduced.addEventListener) reduced.addEventListener('change', onPrefChange);
})();
